import { Exception } from 'common/exceptions/Exception';
import { ClientListItem } from 'common/models/ClientListItem';
import { SupportedOfficeItem } from 'common/utils/SupportedOfficeItem';
import CONFIG from 'config';
import $ from 'jquery';
import * as App from '../app/app';
import { AddInStakeholder } from '../models/AddInStakeholder';
import { DomainDetails } from '../models/DomainDetails';
import { Option } from '../models/Option';
import { ProjectInfo } from '../models/ProjectInfo';
import { Region } from '../models/Region';
import { Stakeholder } from './../models/Stakeholder';
import { getPrimaryStakeholders } from './contactUtils';
import * as OutlookAsyncUtils from './outlookAsyncUtils';
import * as OutlookItemUtils from './outlookItemUtils';
import * as UserSettings from './user-settings';
import {
  EnumClientFeatureAdditionalStatus,
  getSuggestedOrganisationByEmail,
  getOrganisationEnrichmentAdditionalStatus,
  isOrganisationEnrichmentEnabled,
  recordAiEnrichmentAction,
  updateAiEnrichmentConfirmedValue,
} from './api';

const TAG = 'AUI3: ';
export function showMessageAutosaved() {
  $('#was-autosaved-page').show();
}

export function showMessageSaved() {
  $('#was-saved-page').show();
}

export function showSeriesEventNotSupported() {
  $('#series-event-not-supported-page').show();
}

export function showReadOnlySharedItemNotSupported() {
  $('#read-only-shared-item-not-supported-page').show();
}

export async function showPrimaryStakeholdersInfo(item: SupportedOfficeItem) {
  const sharedProperties = await OutlookAsyncUtils.getSharedProperties(item);
  const primaryStakeholders = await getPrimaryStakeholders(
    item,
    sharedProperties
  );

  const names = primaryStakeholders.map((s) => s.fullName).join('<br/>');
  const emails = primaryStakeholders.map((s) => s.email).join('<br/>');

  changeLabelsByItemType(item);

  if (
    !CONFIG.isGraphApiEnabled ||
    item.itemType === 'appointment' ||
    sharedProperties
  ) {
    $('#autoSaveAll').hide();
    $('#autoSaveAllLabel').hide();
    $('#autoSaveAll').removeAttr('checked');

    $('#autoSaveOne').attr('checked', 'checked');
    UserSettings.setAutosave(false);
  }

  $('#detail-page').show();

  $('#user-id').html(names);
  $('#user-sub-text').html(emails);
}

async function triggerOrgEnrichmentForField(
  email: string,
  orgFieldSelector: string,
  wrapperSelector: string,
  spinnerSelector: string,
  labelSelector: string,
  acceptSelector: string,
  rejectSelector: string
): Promise<void> {
  const orgField = $(orgFieldSelector);
  if (!email || orgField.val()) return;

  try {
    const [isEnabled, additionalStatus] = await Promise.all([
      isOrganisationEnrichmentEnabled(),
      getOrganisationEnrichmentAdditionalStatus(),
    ]);

    if (
      !isEnabled ||
      additionalStatus === EnumClientFeatureAdditionalStatus.Disabled
    ) {
      return;
    }

    orgField.prop('disabled', true).hide();
    $(spinnerSelector).show();
    orgField.data('skipDataEnrichment', false);
    const suggestion = await getSuggestedOrganisationByEmail(email);
    $(spinnerSelector).hide();
    orgField.prop('disabled', false);
    if (!suggestion) {
      orgField.prop('disabled', false).show();
      return;
    }

    $(labelSelector).text(suggestion);
    $(wrapperSelector).show();

    $(acceptSelector)
      .off('click')
      .on('click', () => {
        orgField.prop('disabled', false).val(suggestion).show();
        $(wrapperSelector).hide();
        orgField.data('skipDataEnrichment', true);
        const domain = email.split('@')[1];
        if (domain && suggestion) {
          void recordAiEnrichmentAction({
            EnrichmentType: 1,
            EnrichmentContext: domain,
            SuggestedValue: suggestion,
            Action: 1,
          }).catch((e) => console.error(TAG, 'Failed to record AI enrichment accept action:', e));
        }
      });

    $(rejectSelector)
      .off('click')
      .on('click', () => {
        orgField.prop('disabled', false).show();
        $(wrapperSelector).hide();
        orgField.data('skipDataEnrichment', true);
        const domain = email.split('@')[1];
        if (domain && suggestion) {
          orgField.data('rejectionDomain', domain);
          void recordAiEnrichmentAction({
            EnrichmentType: 1,
            EnrichmentContext: domain,
            SuggestedValue: suggestion,
            Action: 2,
          }).catch((e) => console.error(TAG, 'Failed to record AI enrichment reject action:', e));
        }
      });
  } catch (e) {
    $(spinnerSelector).hide();
    orgField.prop('disabled', false).show();
    console.error(TAG, 'Org enrichment failed:', e);
  }
}

async function triggerOrgEnrichment(email: string): Promise<void> {
  return triggerOrgEnrichmentForField(
    email,
    '#item-org',
    '#org-suggestion-wrapper',
    '#org-suggestion-spinner',
    '#org-suggestion-label',
    '#org-suggestion-accept',
    '#org-suggestion-reject'
  );
}

async function triggerOrgEnrichmentForCC(
  email: string,
  index: number
): Promise<void> {
  return triggerOrgEnrichmentForField(
    email,
    `#cc-bcc-org${index}`,
    `#org-suggestion-wrapper-cc-${index}`,
    `#org-suggestion-spinner-cc-${index}`,
    `#org-suggestion-label-cc-${index}`,
    `#org-suggestion-accept-cc-${index}`,
    `#org-suggestion-reject-cc-${index}`
  );
}

export function showStakeholdersForm(
  primaryStakeholders: AddInStakeholder[],
  additionalStakeholders: AddInStakeholder[],
  itemType: string,
  readModeAttachments: Office.AttachmentDetails[] | undefined
) {
  if (itemType === 'appointment') {
    $('#primary-stakeholder-detail').hide();
    $('#save-all-or-primary').hide();
    $('#save-or-cancel-buttons-1').hide();
    $('#cc-bcc-email-main').show();
    $('#cc-bcc-email-main').html('Stakeholders:');
    showAllStakeholders(primaryStakeholders);
    $('input[name="save-all-future-email"]:checked').val('all');
  } else {
    const primaryStakeholder = primaryStakeholders[0];
    $('#item-from').val(primaryStakeholder.email);
    $('#item-org2').val(primaryStakeholder.fullName);
    $('#item-from-fname').val(primaryStakeholder.firstName);
    $('#item-from-lname').val(primaryStakeholder.lastName);

    // Reset suggestion UI for each form load, then trigger enrichment
    $('#org-suggestion-wrapper').hide();
    $('#org-suggestion-spinner').hide();
    $('#item-org').show();
    triggerOrgEnrichment(primaryStakeholder.email).catch(console.error);

    showAdditionalStakeholders(additionalStakeholders);
  }

  if (
    readModeAttachments &&
    readModeAttachments.length > 0 &&
    !OutlookItemUtils.canSaveAttachments()
  ) {
    $('#attachments-warning').show();
  } else {
    $('#attachments-warning').hide();
  }
}

// Admin Settings Gear Icon Handler
export function initializeAdminSettingsGear() {
  $('#admin-settings-gear').on('click', function (e) {
    e.stopPropagation();
    console.log('Admin Settings clicked');
    toggleAdminMenu();
  });

  // Close menu when clicking outside
  $(document).on('click', function () {
    hideAdminMenu();
  });

  // Prevent menu from closing when clicking inside it
  $(document).on('click', '.admin-settings-menu', function (e) {
    e.stopPropagation();
  });
}

function toggleAdminMenu() {
  const menu = $('.admin-settings-menu');
  if (menu.is(':visible')) {
    hideAdminMenu();
  } else {
    showAdminMenu();
  }
}

function showAdminMenu() {
  // Remove any existing menu to prevent duplicates
  $('.admin-settings-menu').remove();

  // Create fresh menu
  createAdminMenu();

  // Position menu relative to the gear icon using position() for better reliability
  const gear = $('#admin-settings-gear');
  const gearPosition = gear.position();

  $('.admin-settings-menu')
    .css({
      top: gearPosition.top + gear.outerHeight() + 5,
      right: '12px',
      left: 'auto',
    })
    .show();
}

function hideAdminMenu() {
  $('.admin-settings-menu').hide();
}

function createAdminMenu() {
  const menuHtml = `
    <div class="admin-settings-menu">
      <div class="admin-menu-item" data-action="view-autosaved-contacts">
        <span>View Autosaved Contacts</span>
      </div>
    </div>
  `;

  $('body').append(menuHtml);

  // Handle menu item clicks
  $('.admin-menu-item').on('click', function () {
    const action = $(this).data('action');
    handleAdminMenuAction(action);
    hideAdminMenu();
  });
}

function getSiteUrl(): string {
  const domain = UserSettings.getDomain();
  const region = CONFIG.regions.find((r) => r.api_url === domain);
  return region?.site_url;
}

function handleAdminMenuAction(action: string) {
  switch (action) {
    case 'view-autosaved-contacts':
      console.log('Opening view-autosaved-contacts...');
      const domainUrl = getSiteUrl();

      if (!domainUrl) {
        showInfoDialog('Domain not configured. Please log in first.');
        return;
      }

      const autosavedContactsUrl = `${domainUrl}/new-ui/admin/email-plugin?section=management&activeTab=Email%20Plugins&parent=Account%20Configuration`;

      try {
        const newWindow = window.open(autosavedContactsUrl, '_blank');
        if (
          !newWindow ||
          newWindow.closed ||
          typeof newWindow.closed === 'undefined'
        ) {
          console.error('Popup blocker likely prevented opening');
        }
      } catch (error) {
        console.error('Failed to open autosaved contacts URL:', error);
        showInfoDialog(
          'Failed to open autosaved contacts page. Please try again or contact support.'
        );
      }
      break;
    case 'about':
      console.log('Opening about...');
      showAboutDialog();
      break;
    default:
      console.log('Unknown action:', action);
  }
}

function showAboutDialog() {
  const aboutContent = `
    <div style="text-align: center;">
      <h3>Darzin Outlook Add-in</h3>
      <p>Version: 2022-05-10 13:41</p>
      <p>© Microsoft Corporation</p>
      <p style="margin-top: 15px; font-size: 14px; color: #666;">
        Stakeholder management integration for Outlook
      </p>
    </div>
  `;

  $('#dialog-info p').html(aboutContent);
  $('#dialog-info').show();
}

export function showAdditionalStakeholders(stakeholders: AddInStakeholder[]) {
  let additionalStakeholdersHtml = '';

  let index = 0;
  stakeholders.forEach((stakeholder) => {
    additionalStakeholdersHtml += getStkFormAdd(index++, stakeholder, false);
  });

  $('#cc-bcc-emails').html(additionalStakeholdersHtml);

  stakeholders.forEach((stakeholder, idx) => {
    if (stakeholder.email) {
      triggerOrgEnrichmentForCC(stakeholder.email, idx).catch(console.error);
    }
  });

  if (stakeholders.length > 0) {
    $('#cc-bcc-email-main').show();
    $('#secondSubmit').show();
  } else {
    $('#cc-bcc-email-main').hide();
    $('#secondSubmit').hide();
  }
}

export function showAllStakeholders(stakeholders: AddInStakeholder[]) {
  let additionalStakeholdersHtml = '';

  let index = 0;
  stakeholders.forEach((stakeholder) => {
    additionalStakeholdersHtml += getStkFormAdd(index++, stakeholder, true);
  });

  $('#cc-bcc-emails').html(additionalStakeholdersHtml);

  stakeholders.forEach((stakeholder, idx) => {
    if (stakeholder.email) {
      triggerOrgEnrichmentForCC(stakeholder.email, idx).catch(console.error);
    }
  });

  if (stakeholders.length > 0) {
    $('#secondSubmit').show();
  } else {
    $('#secondSubmit').hide();
  }
}

function getStkFormAdd(
  index: number,
  stakeholder: AddInStakeholder,
  showCheckboxes: boolean
) {
  let html =
    '<div class="cc-bcc-single-details">\
                <table class="table">';

  if (showCheckboxes) {
    html +=
      '<tr>\
         <td class="label">Save this stakeholder</td>\
         <td><input type="checkbox" class="cc-check" data-id="' +
      index +
      '"  placeholder="" checked  name="cc-check' +
      index +
      '" id="cc-check' +
      index +
      '"/></td>\
       </tr>';
  }
  html +=
    '<tr>\
              <td class="label">Individual/Organisation</td>\
              <td>\
                  <div class="select-wrap">\
                      <span class="down-arrow"></span>\
                      <select class="form-controller cc-bcc-select cc_stakeholder_type" data-id="' +
    index +
    '" id="cc_stakeholder_type' +
    index +
    '" name="cc_stakeholder_type' +
    index +
    '">\
                          <option value="Individual">Individual</option>\
                          <option value="Organisation">Organisation</option>\
                      </select>\
                  </div>\
              </td>\
          </tr>\
          <tr>\
              <td class="label">Email</td>\
              <td class="emailaddress">' +
    stakeholder.email +
    '</td>\
          </tr>\
          <tr class="table-indi-fields">\
              <td class="label">First Name</td>\
              <td>\
                  <div class="input-field">\
                      <input type="text" class="form-controller cc-bcc-fname" placeholder="" value="' +
    stakeholder.firstName +
    '" id="cc-bcc-fname' +
    index +
    '" name="cc-bcc-fname' +
    index +
    '"/>\
                  </div>\
              </td>\
          </tr>\
          <tr class="table-indi-fields">\
              <td class="label">Last Name</td>\
              <td>\
                  <div class="input-field">\
                      <input type="text" class="form-controller cc-bcc-lname" placeholder="" value="' +
    stakeholder.lastName +
    '" id="cc-bcc-lname' +
    index +
    '" name="cc-bcc-lname' +
    index +
    '"/>\
                  </div>\
              </td>\
          </tr>\
          <tr class="table-indi-fields">\
              <td class="label">Organisation</td>\
              <td>\
                  <div class="input-field">\
                      <input type="text" class="form-controller cc-bcc-org" placeholder="" value=""' +
    ' id="cc-bcc-org' +
    index +
    '" name="cc-bcc-org' +
    index +
    '"/>' +
    '<div id="org-suggestion-wrapper-cc-' +
    index +
    '" class="org-suggestion-wrapper" style="display:none;">' +
    '<div class="action-chip">' +
    '<svg class="chip-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M4.375 2.33333C4.375 2.01117 4.11383 1.75 3.79167 1.75C3.4695 1.75 3.20833 2.01117 3.20833 2.33333V3.20833H2.33333C2.01117 3.20833 1.75 3.4695 1.75 3.79167C1.75 4.11383 2.01117 4.375 2.33333 4.375H3.20833V5.25C3.20833 5.57217 3.4695 5.83333 3.79167 5.83333C4.11383 5.83333 4.375 5.57217 4.375 5.25V4.375H5.25C5.57217 4.375 5.83333 4.11383 5.83333 3.79167C5.83333 3.4695 5.57217 3.20833 5.25 3.20833H4.375V2.33333Z" fill="white"/>' +
    '<path d="M7.82246 3.0809C7.73824 2.86192 7.42843 2.86192 7.3442 3.0809L6.20083 6.05367C6.1748 6.12133 6.12133 6.1748 6.05367 6.20083L3.0809 7.3442C2.86192 7.42843 2.86192 7.73824 3.0809 7.82246L6.05367 8.96584C6.12133 8.99186 6.1748 9.04533 6.20083 9.11299L7.3442 12.0858C7.42843 12.3047 7.73824 12.3047 7.82246 12.0858L8.96584 9.11299C8.99186 9.04533 9.04533 8.99186 9.11299 8.96584L12.0858 7.82246C12.3047 7.73824 12.3047 7.42843 12.0858 7.3442L9.11299 6.20083C9.04533 6.1748 8.99186 6.12133 8.96584 6.05367L7.82246 3.0809Z" fill="white"/>' +
    '</svg>' +
    '<span id="org-suggestion-label-cc-' + index + '" class="action-chip-text"></span>' +
    '<button id="org-suggestion-accept-cc-' + index + '" class="chip-action-btn" type="button" title="Accept Suggestion" aria-label="Accept suggestion">' +
    '<svg width="12" height="12" viewBox="0 0 24 24" fill="rgba(255,255,255,1)" xmlns="http://www.w3.org/2000/svg"><path d="M9.9997 15.1709L19.1921 5.97852L20.6063 7.39273L9.9997 17.9993L3.63574 11.6354L5.04996 10.2212L9.9997 15.1709Z"/></svg>' +
    '</button>' +
    '<button id="org-suggestion-reject-cc-' + index + '" class="chip-action-btn" type="button" title="Reject Suggestion" aria-label="Reject suggestion">' +
    '<svg width="12" height="12" viewBox="0 0 24 24" fill="rgba(255,255,255,1)" xmlns="http://www.w3.org/2000/svg"><path d="M11.9997 10.5865L16.9495 5.63672L18.3637 7.05093L13.4139 12.0007L18.3637 16.9504L16.9495 18.3646L11.9997 13.4149L7.04996 18.3646L5.63574 16.9504L10.5855 12.0007L5.63574 7.05093L7.04996 5.63672L11.9997 10.5865Z"/></svg>' +
    '</button>' +
    '</div></div>' +
    '<div id="org-suggestion-spinner-cc-' + index + '" class="org-suggestion-spinner" style="display:none;">' +
    '<div class="enrichment-spinner"></div></div>' +
    '\
                  </div>\
              </td>\
          </tr>\
          <tr class="table-org-fields" style="display: none;">\
              <td class="label">Organisation</td>\
              <td>\
                  <div class="input-field">\
                      <input type="text" class="form-controller cc-bcc-org2" placeholder="" value="' +
    stakeholder.fullName +
    '" id="cc-bcc-org2' +
    index +
    '" name="cc-bcc-org2' +
    index +
    '"/>\
                  </div>\
              </td>\
          </tr>\
          <tr class="table-indi-fields">\
              <td class="label">Position</td>\
              <td>\
                  <div class="input-field">\
                      <input type="text" class="form-controller cc-bcc-pos" placeholder=""' +
    ' value="" id="cc-bcc-pos' +
    index +
    '" name="cc-bcc-pos' +
    index +
    '"/>\
                  </div>\
              </td>\
          </tr>\
          <tr class="">\
              <td class="label">Phone</td>\
              <td>\
                  <div class="input-field">\
                      <input type="text" class="form-controller cc-bcc-mobile" placeholder=""' +
    '  value="" id="cc-bcc-mobile' +
    index +
    '" name="cc-bcc-mobile' +
    index +
    '"/>\
                  </div>\
              </td>\
          </tr>\
      </table>\
  </div>';

  return html;
}

export function getStakeholdersFromUi() {
  const stakeholders = Array<Stakeholder>();
  const itemEmail = getStringValueOrNull('#item-from');
  const itemPos = getStringValueOrNull('#item-pos');
  const contactGroupsArray = getMultiSelectValues('#item-contact-group');
  const stakeholderType = getStringValueOrNull('#select-individual-main');
  // Auto-accept pending org suggestion if user saves without explicitly confirming
  if ($('#org-suggestion-wrapper').is(':visible')) {
    const pendingSuggestion = $('#org-suggestion-label').text();
    if (pendingSuggestion) {
      $('#item-org').val(pendingSuggestion).show();
      $('#org-suggestion-wrapper').hide();
      $('#item-org').data('skipDataEnrichment', true);
    }
  }

  let itemOrg = getStringValueOrNull('#item-org');
  if (stakeholderType != 'Individual') {
    itemOrg = getStringValueOrNull('#item-org2');
  }
  const primaryRejectionDomain = $('#item-org').data('rejectionDomain') as string | undefined;
  if (stakeholderType === 'Individual' && primaryRejectionDomain && itemOrg) {
    void updateAiEnrichmentConfirmedValue({ EnrichmentType: 1, EnrichmentContext: primaryRejectionDomain, ConfirmedValue: itemOrg }).catch((e) =>
      console.error(TAG, 'Failed to update AI enrichment confirmed value:', e)
    );
  }
  $('#item-org').removeData('rejectionDomain');
  const fname = getStringValueOrNull('#item-from-fname');
  const lname = getStringValueOrNull('#item-from-lname');
  const itemMobile = getStringValueOrNull('#item-mobile');
  const additionalSharedProjectIDs =
    $('#save-project')
      .select2('data')
      ?.map((i) => i.id) || [];

  const primaryStakeholder: Stakeholder = {
    ID: undefined,
    StakeholderType: stakeholderType ?? undefined,
    FirstName: stakeholderType === 'Individual' ? fname : itemOrg,
    LastName: stakeholderType === 'Individual' ? lname : undefined,
    Email: itemEmail,
    ContactGroupIDs: contactGroupsArray,
    Organisations: [
      {
        Name: stakeholderType != 'Individual' ? '' : itemOrg,
        Position: stakeholderType != 'Individual' ? '' : itemPos,
        IsFavourite: stakeholderType == 'Individual' ? true : false,
      },
    ],
    MobileNumber: undefined,
    WorkPhoneNumber: undefined,
    AdditionalSharedProjectIDs: additionalSharedProjectIDs,
    SkipDataEnrichment: !!$('#item-org').data('skipDataEnrichment'),
  };
  if (stakeholderType != 'Individual') {
    primaryStakeholder.WorkPhoneNumber = itemMobile;
  } else {
    primaryStakeholder.MobileNumber = itemMobile;
  }

  if (primaryStakeholder.Email) {
    stakeholders.push(primaryStakeholder);
  }

  if ($('input[name="save-all-future-email"]:checked').val() == 'all') {
    $('.cc-bcc-single-details').each(function (idx) {
      const saveCheckbox = $(this).find('.cc-check');
      if (saveCheckbox.length > 0 && !saveCheckbox.prop('checked')) {
        return;
      }

      // Auto-accept pending CC org suggestion if user saves without confirming
      const ccWrapper = $(`#org-suggestion-wrapper-cc-${idx}`);
      if (ccWrapper.is(':visible')) {
        const pendingSuggestion = $(`#org-suggestion-label-cc-${idx}`).text();
        if (pendingSuggestion) {
          const ccOrgField = $(this).find('.cc-bcc-org');
          ccOrgField.val(pendingSuggestion).show();
          ccWrapper.hide();
          ccOrgField.data('skipDataEnrichment', true);
        }
      }

      const itemEmailTo = $(this).find('.emailaddress').html();
      const ccOrgField = $(this).find('.cc-bcc-org');
      let itemOrgTo = '';
      if ($(this).find('.cc_stakeholder_type').val() == 'Individual') {
        itemOrgTo = ccOrgField.val().toString();
      } else {
        itemOrgTo = $(this).find('.cc-bcc-org2').val().toString();
      }
      const ccRejectionDomain = ccOrgField.data('rejectionDomain') as string | undefined;
      if (ccRejectionDomain && itemOrgTo) {
        void updateAiEnrichmentConfirmedValue({ EnrichmentType: 1, EnrichmentContext: ccRejectionDomain, ConfirmedValue: itemOrgTo });
        ccOrgField.removeData('rejectionDomain');
      }
      const itemPosTo = $(this).find('.cc-bcc-pos').val().toString();
      const itemMobileTo = $(this).find('.cc-bcc-mobile').val().toString();
      const StakeholderTypeTo = $(this)
        .find('.cc_stakeholder_type')
        .val()
        ?.toString();
      const fnameTo = $(this).find('.cc-bcc-fname').val().toString();
      const lnameTo = $(this).find('.cc-bcc-lname').val().toString();
      const additionalStakeholder: Stakeholder = {
        ID: undefined,
        StakeholderType: StakeholderTypeTo,
        FirstName: StakeholderTypeTo != 'Individual' ? itemOrgTo : fnameTo,
        LastName: lnameTo,
        Email: itemEmailTo,
        ContactGroupIDs: contactGroupsArray,
        Organisations: [
          {
            Name: StakeholderTypeTo != 'Individual' ? '' : itemOrgTo,
            Position: StakeholderTypeTo != 'Individual' ? '' : itemPosTo,
            IsFavourite: StakeholderTypeTo == 'Individual' ? true : false,
          },
        ],
        MobileNumber: undefined,
        WorkPhoneNumber: undefined,
        AdditionalSharedProjectIDs: additionalSharedProjectIDs,
        SkipDataEnrichment: !!$(this).find('.cc-bcc-org').data('skipDataEnrichment'),
      };
      if (StakeholderTypeTo != 'Individual') {
        additionalStakeholder.WorkPhoneNumber = itemMobileTo;
      } else {
        additionalStakeholder.MobileNumber = itemMobileTo;
      }
      stakeholders.push(additionalStakeholder);
    });
  }
  return stakeholders;
}

function changeLabelsByItemType(item: SupportedOfficeItem) {
  if (item.itemType === 'appointment') {
    const labels = [
      '#login-text',

      '#autoSaveAllLabel',
      '#autoSaveOneLabel',

      '#save-email-detail',
      '#save-email-detail2',

      '#item-saved-successfully',
    ];

    let tooltip = $('#futureAllTooltip').attr('title');
    if (tooltip) {
      tooltip = tooltip.replace('email', 'event');
    }
    $('#futureAllTooltip').attr('title', tooltip);

    $('#div-interaction-method').hide();

    labels.forEach((l) => changeLabelByItemType(l));
  }
}

function changeLabelByItemType(id: string) {
  let currentLabel = $(id).html();
  if (currentLabel) {
    currentLabel = currentLabel.replace('email', 'event');
    currentLabel = currentLabel.replace('Email', 'Event');
  }
  $(id).html(currentLabel);
}

export function setIcon(path: string, width: number) {
  $('#header-logo').attr('width', width);
  $('#header-logo').attr('src', path);
}

export function setAppName(name: string) {
  const elements = ['#login-text', '#item-saved-successfully'];
  for (const el of elements) {
    setAppNameInElement(el, name);
  }
}

export function setFreeTrialUrl(url: string) {
  const id = '#login-text__free-trial';
  $(id).attr('href', url);
}

export function setOptions(
  isComplaintsEnabled: boolean,
  isConfidentialOptionsEnabled: boolean
) {
  if (isComplaintsEnabled) $('#div-interaction-method').show();
  else $('#div-interaction-method').hide();

  if (isConfidentialOptionsEnabled) $('#form-confidentiality').show();
  else {
    $('#form-confidentiality').hide();
  }
}

function setAppNameInElement(id: string, name: string) {
  let html = $(id).html();
  html = html.replace(/APP_NAME/g, name);
  $(id).html(html);
}

export function renderOptions(
  options: Option[],
  selector: string,
  addEmptyOption = false
) {
  let html = '';
  if (addEmptyOption) {
    html = '<option value="" selected>None</option>';
  }

  options.forEach(function (item) {
    html += '<option value="' + item.Value + '">' + item.Text + '</option>';
  });

  $(selector).html(html);

  if (html == '') {
    $(selector.replace('#', '.') + '-main').hide();
  } else {
    $(selector.replace('#', '.') + '-main').show();
  }
}

export function showRegions(regions: Region[]) {
  const regionSelect = $('#select-domain-login');

  const domain = UserSettings.getDomain();

  let selected = false;
  let options = '';
  regions.forEach(function (item) {
    if (domain == item.api_url) {
      selected = true;
      options +=
        '<option value="' +
        item.api_url +
        '" selected>' +
        item.label +
        '</option>';
    } else {
      options +=
        '<option value="' + item.api_url + '">' + item.label + '</option>';
    }
  });

  if (!selected) {
    options =
      '<option value="" disabled selected>Select Region</option>' + options;
  }

  regionSelect.html(options);
}

export function showErrorInForm(message: string, error?: Exception) {
  $('#loader').hide();

  $('#error__message-details').hide();

  if (error) {
    let errorDetails: string;
    if (error instanceof Error) {
      // instance of "Error" cannot be stringified, just show the stack. it will include the error message.
      errorDetails = error.stack;
    } else {
      errorDetails = JSON.stringify(error);
    }

    $('#error__message-details').html('<br/>' + errorDetails);

    $('#error__btn-details').show();
  } else {
    $('#error__btn-details').hide();
  }
  $('#error__message').html(message);

  $('#error-dialog').show();
}

export function showInfoDialog(message: string) {
  $('#loader').hide();
  // .text() not .html(): messages may contain user content (XSS).
  $('#dialog-info p').text(message);
  $('#dialog-info').show();
}

export function hideInfoDialog() {
  $('#dialog-info').hide();
}

export function showAuthErrorDialog(message: string | null) {
  $('#loader').hide();
  $('#detail-save-error p').html(message);
  $('#detail-save-error').show();
}

export function showLogoutSuccessDialog() {
  $('#loader').hide();
  $('#dialog-logout-success').show();
}

export function refreshClients(clients: ClientListItem[]) {
  const clientOption = [];
  let hasDefault = false;

  for (let index = 0; index < clients.length; index++) {
    if (clients[index].IsCurrent) {
      hasDefault = true;
      clientOption.push(
        `<option value="${clients[index].ClientID}" selected>${clients[index].Name}</option>`
      );
    } else {
      clientOption.push(
        `<option value="${clients[index].ClientID}">${clients[index].Name}</option>`
      );
    }
  }

  if (clients.length > 1 && !hasDefault) {
    clientOption.unshift(
      '<option value="" disabled selected>select account</option>'
    );
  }
  $('#save-client').html(clientOption.join(''));

  if (!hasDefault) {
    const clientOptions = $('#save-client').find('option');
    const defaultOption =
      clientOptions.length > 1 ? clientOptions[1] : clientOptions[0];

    defaultOption.setAttribute('selected', 'true');
  }
  $('#save-client').trigger('change');
}

export function refreshDomains(
  domains: DomainDetails[],
  favouriteDomainAndGroup: ProjectInfo,
  projectGroupAvailable: boolean
) {
  let domainOption = '';
  if (domains.length > 1) {
    domainOption = '<option value="" disabled selected>select domain</option>';
  }
  if (favouriteDomainAndGroup) {
    domainOption = '<option value="" disabled >select domain</option>';
  }
  for (let index = 0; index < domains.length; index++) {
    domainOption +=
      '<option value="' +
      domains[index].ID +
      '"' +
      '>' +
      domains[index].DomainName +
      '</option>';
  }
  $('#save-domain').html(domainOption);
  if (favouriteDomainAndGroup) {
    $('#save-domain').val(favouriteDomainAndGroup.domainId).trigger('change');
    if (projectGroupAvailable) {
      $('#save-proj-group')
        .val(favouriteDomainAndGroup.projectGroupId)
        .trigger('change');
    }
    $('#save-project').val(favouriteDomainAndGroup.projectId).trigger('change');
  } else {
    const domainOptions = $('#save-domain').find('option');
    // select the first domain OR the placeholder option
    const defaultOption =
      domainOptions.length > 1 ? domainOptions[1] : domainOptions[0];

    defaultOption.setAttribute('selected', 'true');
    $('#save-domain').trigger('change');
  }

  $('#form-domain').show();
  $('#ProjectGroupContainer').show();
  $('#form-project').show();

  // if there is only one domain, hide the dropdown
  if (domains.length == 1) {
    $('#form-domain').hide();
    const domain = domains[0];

    if (projectGroupAvailable) {
      const groupsWithAccessibleProjects = domain.ProjectGroups.filter(
        (g) => g.Projects && g.Projects.length
      );
      // if there is only one project group, select it and hide the dropdown
      if (groupsWithAccessibleProjects.length == 1) {
        const group = groupsWithAccessibleProjects[0];
        if (!favouriteDomainAndGroup) {
          $('#save-proj-group').val(group.ID).trigger('change');
        }
        $('#ProjectGroupContainer').hide();

        // if there is only one project within the only one project group, hide the dropdown and select the project
        if (group.Projects.length == 1 && !favouriteDomainAndGroup) {
          $('#form-project').hide();
          $('#save-project').val(group.Projects[0].ID).trigger('change');
        }
      }
    } else {
      $('#ProjectGroupContainer').hide();
      // if no project groups and there is only one project, hide the dropdown and select the project
      if (domain.Projects.length == 1 && !favouriteDomainAndGroup) {
        $('#form-project').hide();
        $('#save-project').val(domain.Projects[0].ID).trigger('change');
      }
    }
  }
}

export function checkSaveForm(): boolean {
  console.log(TAG, 'Validating the form');
  const projectGroup = $('#save-proj-group').val();
  const project = $('#save-project').val();
  let priorities = false;
  let assignedTo = false;
  if ($('#item-communication').val() == 'Complaint') {
    priorities = $('#complaint-priorities').val() ? false : true;
    assignedTo = $('#item-assigned-to').val() ? false : true;
  }

  if (
    (projectGroup == null || projectGroup == '') &&
    App.projectGroupAvailable
  ) {
    $('#project-group-error').html('Please select Project Group');
    $('#save-proj-group').focus();
    console.log(TAG, 'Validation not passed: project group');
    return false;
  } else if (project == null || project == '') {
    $('#project-group-error').html('');
    $('#project-error').html('Please select Project');
    $('#save-project').focus();
    console.log(TAG, 'Validation not passed: project');
    return false;
  } else if (CONFIG.isComplaintsEnabled && assignedTo) {
    $('#item-assigned-to-error').html('Please select Assign To');
    $('#item-assigned-to').focus();
    console.log(TAG, 'Validation not passed: item assigned to');
    return false;
  } else if (CONFIG.isComplaintsEnabled && priorities) {
    $('#complaint-priorities-error').html('Please select Complaint Priorities');
    $('#complaint-priorities').focus();
    console.log(TAG, 'Validation not passed: complaint priorities');
    return false;
  } else {
    $('#project-group-error').html('');
    $('#project-error').html('');
    $('#item-assigned-to-error').html('');
    $('#complaint-priorities-error').html('');
    console.log(TAG, 'Validation passed');
    return true;
  }
}

export function hidePages() {
  $('#welcome-pg').hide();

  $('#series-event-not-supported-page').hide();
  $('#was-saved-page').hide();
  $('#was-autosaved-page').hide();

  $('#login-page').hide();
  $('#detail-page').hide();
  hideDocumentDetailsPage();
  $('#detail-final').hide();
  $('#item-not-supported-page').hide();
}

export function getStringValueOrNull(id: string) {
  const value = $(id).val();
  if (value) {
    return value.toString();
  } else {
    return null;
  }
}

export function getMultiSelectValues(id: string): string[] {
  const data = $(id).select2('data');

  return data.map((value) => value.id);
}

export function hideDocumentDetailsPage() {
  $('#detail-final').hide();
}

export function showSaveSuccessfulDialog() {
  $('#detail-save-pop').show();
}

export function hideSaveSuccessfulDialog() {
  $('#detail-save-pop').hide();
}

export function showItemNotSupportedPage() {
  $('#item-not-supported-page').show();
}
